import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import Swal from 'sweetalert2';
import { ProjectManagementService } from '../../projects/project-management.service';
import { TodoService } from '../todo.service';

interface TodoDetailDialogData {
  type: 'task' | 'todo';
  item: any;
}

@Component({
  selector: 'app-todo-detail-dialog',
  templateUrl: './todo-detail-dialog.component.html',
  styleUrls: ['./todo-detail-dialog.component.scss']
})
export class TodoDetailDialogComponent implements OnInit {
  loading = false;
  saving = false;
  commentSaving = false;
  comments: any[] = [];
  commentText = '';

  form = this.fb.group({
    title: ['', Validators.required],
    description: [''],
    status: ['Pending', Validators.required],
    priority: ['Medium', Validators.required],
    progressPercent: [0],
    dueDate: ['']
  });

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<TodoDetailDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TodoDetailDialogData,
    private projectService: ProjectManagementService,
    private todoService: TodoService
  ) {}

  ngOnInit(): void {
    this.patchForm(this.data.item);
    this.loadComments();
  }

  get isTask(): boolean {
    return this.data.type === 'task';
  }

  loadComments(): void {
    this.loading = true;
    const request = this.isTask
      ? this.projectService.getTaskComments(this.data.item.id)
      : this.todoService.getComments(this.data.item.id);

    request.subscribe({
      next: (comments) => {
        this.comments = comments || [];
        this.loading = false;
      },
      error: () => {
        this.comments = [];
        this.loading = false;
      }
    });
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const payload = {
      title: this.form.value.title,
      description: this.form.value.description,
      status: this.form.value.status,
      priority: this.form.value.priority,
      dueDate: this.form.value.dueDate || null,
      ...(this.isTask ? { progressPercent: Number(this.form.value.progressPercent || 0) } : {})
    };

    this.saving = true;
    const request = this.isTask
      ? this.projectService.updateProjectTask(this.data.item.id, payload)
      : this.todoService.update(this.data.item.id, payload);

    request.subscribe({
      next: () => {
        this.saving = false;
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.saving = false;
        Swal.fire('Error', err?.error?.message || 'Failed to update item.', 'error');
      }
    });
  }

  addComment(): void {
    const comment = this.commentText.trim();
    if (!comment) return;

    this.commentSaving = true;
    const request = this.isTask
      ? this.projectService.addTaskComment(this.data.item.id, { comment })
      : this.todoService.addComment(this.data.item.id, { comment });

    request.subscribe({
      next: () => {
        this.commentText = '';
        this.commentSaving = false;
        this.loadComments();
      },
      error: (err) => {
        this.commentSaving = false;
        Swal.fire('Error', err?.error?.message || 'Failed to post comment.', 'error');
      }
    });
  }

  private patchForm(item: any): void {
    this.form.patchValue({
      title: item?.title || '',
      description: item?.description || '',
      status: item?.status || 'Pending',
      priority: item?.priority || 'Medium',
      progressPercent: Number(item?.progressPercent || 0),
      dueDate: item?.dueDate ? this.toDateInput(item.dueDate) : ''
    });
  }

  private toDateInput(value: string): string {
    const date = new Date(value);
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
